import React from 'react';
import './HumanTaskDisplay.scss';

interface PhoneCallTaskDetails {
  phoneNumber: string;
  contactName?: string;
  purpose: string;
  scriptGuidance?: string;
  expectedDuration?: number;
}

interface WebActionTaskDetails {
  url: string;
  actionType: 'form_fill' | 'booking' | 'information_retrieval' | 'other';
  instructions: string;
  formFields?: Record<string, string>;
  expectedOutcome?: string;
}

type HumanTaskDetails = PhoneCallTaskDetails | WebActionTaskDetails;

interface HumanTaskDisplayProps {
  taskId: string;
  taskType: 'phone_call' | 'web_action';
  taskTypeLabel: string;
  title: string;
  description: string;
  taskDetails: HumanTaskDetails;
  status: string;
  deadline?: string;
}

const HumanTaskDisplay: React.FC<HumanTaskDisplayProps> = ({
  taskId,
  taskType,
  taskTypeLabel,
  title,
  description,
  taskDetails,
  status,
  deadline,
}) => {
  const isPhoneCall = taskType === 'phone_call';
  const phoneDetails = isPhoneCall
    ? (taskDetails as PhoneCallTaskDetails)
    : null;
  const webDetails = !isPhoneCall
    ? (taskDetails as WebActionTaskDetails)
    : null;

  return (
    <div className='human-task-display'>
      <div className='task-header'>
        <span className='task-type-badge'>{taskTypeLabel}</span>
        <span className='task-status'>{status}</span>
      </div>

      <h3 className='task-title'>{title}</h3>
      {deadline && (
        <div className='task-deadline'>
          <span className='deadline-label'>Deadline:</span>
          <span className='deadline-value'>
            {new Date(deadline).toLocaleString('en-GB', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </span>
        </div>
      )}
      <p className='task-description'>{description}</p>

      <div className='task-details'>
        {isPhoneCall && phoneDetails && (
          <>
            <div className='detail-row'>
              <span className='detail-label'>Phone Number:</span>
              <span className='detail-value'>{phoneDetails.phoneNumber}</span>
            </div>
            {phoneDetails.contactName && (
              <div className='detail-row'>
                <span className='detail-label'>Contact Name:</span>
                <span className='detail-value'>{phoneDetails.contactName}</span>
              </div>
            )}
            <div className='detail-row'>
              <span className='detail-label'>Purpose:</span>
              <span className='detail-value'>{phoneDetails.purpose}</span>
            </div>
            {phoneDetails.scriptGuidance && (
              <div className='detail-row'>
                <span className='detail-label'>Script Guidance:</span>
                <span className='detail-value script-guidance'>
                  {phoneDetails.scriptGuidance}
                </span>
              </div>
            )}
          </>
        )}

        {!isPhoneCall && webDetails && (
          <>
            <div className='detail-row'>
              <span className='detail-label'>URL:</span>
              <a
                href={webDetails.url}
                target='_blank'
                rel='noopener noreferrer'
                className='detail-value detail-link'
              >
                {webDetails.url}
              </a>
            </div>
            <div className='detail-row'>
              <span className='detail-label'>Action Type:</span>
              <span className='detail-value'>
                {webDetails.actionType.replace(/_/g, ' ')}
              </span>
            </div>
            <div className='detail-row'>
              <span className='detail-label'>Instructions:</span>
              <span className='detail-value script-guidance'>
                {webDetails.instructions}
              </span>
            </div>
            {webDetails.formFields &&
              Object.keys(webDetails.formFields).length > 0 && (
                <div className='detail-row'>
                  <span className='detail-label'>Form Fields:</span>
                  <div className='detail-value form-fields'>
                    {Object.entries(webDetails.formFields).map(
                      ([key, value]) => (
                        <div key={key} className='form-field'>
                          <strong>{key}:</strong> {value}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            {webDetails.expectedOutcome && (
              <div className='detail-row'>
                <span className='detail-label'>Expected Outcome:</span>
                <span className='detail-value'>
                  {webDetails.expectedOutcome}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HumanTaskDisplay;
